/**
 * FunnelChart - Visualizes lead pipeline stages as a funnel
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';

const STAGE_COLORS = {
  new: '#6B7280',
  contacted: '#3B82F6',
  tour_scheduled: '#8B5CF6',
  tour_completed: '#10B981',
  enrollment_pending: '#F59E0B',
  enrolled: '#059669',
  lost: '#EF4444',
  new_lead: '#6B7280',
  meeting_scheduled: '#F59E0B',
  application_submitted: '#10B981',
  deferred: '#6B7280',
};

const STAGE_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  tour_scheduled: 'Tour Scheduled',
  tour_completed: 'Tour Completed',
  enrollment_pending: 'Enrollment Pending',
  enrolled: 'Enrolled',
  lost: 'Lost',
  new_lead: 'New Lead',
  meeting_scheduled: 'Meeting Scheduled',
  application_submitted: 'Application Submitted',
  deferred: 'Deferred',
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="font-medium text-gray-900">{STAGE_LABELS[data.stage] || data.stage}</p>
        <p className="text-sm text-gray-600">Count: {data.count}</p>
        {data.percentage !== undefined && (
          <p className="text-sm text-gray-600">Percentage: {data.percentage}%</p>
        )}
        {data.value !== undefined && data.value > 0 && (
          <p className="text-sm text-gray-600">Value: ${data.value.toLocaleString()}</p>
        )}
      </div>
    );
  }
  return null;
};

const FunnelChart = ({ data = [], isLoading = false }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No funnel data available
      </div>
    );
  }

  // Sort data by standard pipeline order
  const pipelineOrder = [
    'new',
    'new_lead',
    'contacted',
    'tour_scheduled',
    'tour_completed',
    'meeting_scheduled',
    'enrollment_pending',
    'application_submitted',
    'enrolled',
  ];

  const sortedData = [...data].sort((a, b) => {
    const indexA = pipelineOrder.indexOf(a.stage);
    const indexB = pipelineOrder.indexOf(b.stage);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  // Filter out terminal stages from main funnel
  const funnelData = sortedData.filter(
    (item) => !['lost', 'deferred', 'archived'].includes(item.stage)
  );

  // Add display labels
  const chartData = funnelData.map((item) => ({
    ...item,
    label: STAGE_LABELS[item.stage] || item.stage,
    fill: STAGE_COLORS[item.stage] || '#6B7280',
  }));

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" hide />
          <YAxis
            dataKey="label"
            type="category"
            width={140}
            tick={{ fontSize: 12, fill: '#4B5563' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={32}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
            <LabelList
              dataKey="count"
              position="right"
              formatter={(value) => `${value} leads`}
              style={{ fill: '#6B7280', fontSize: 12 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default FunnelChart;
